import React, { useEffect } from 'react';

// Internal modules
import { CI, features } from '@/lib';
import { useStoreState } from '@/store';
import { PaypalThemedProvider } from '@/styles';
import {
  ButtonPPReactV7,
  RebrandButtonT1,
  RebrandButtonT2,
  RebrandButtonT3,
} from './StyledComponents';
import { logFPTICTAContentKey } from './fpti';
import { useInterstitialOpen, useSubmitButtonContent } from './hooks';
import type { ButtonProps } from './types';
import { REBRAND_BUTTON_TREATMENT } from '@/types';

const getComponent = (isFormSubmission: boolean) => {
  if (!isFormSubmission) {
    // Only test the PayPal 5.1 rebrand button colors on main CTA
    return ButtonPPReactV7;
  }

  const REBRAND_TREATMENT = features.get('rebrandButtonColor');

  if (REBRAND_TREATMENT === REBRAND_BUTTON_TREATMENT.Treatment1) {
    return RebrandButtonT1;
  }
  if (REBRAND_TREATMENT === REBRAND_BUTTON_TREATMENT.Treatment2) {
    return RebrandButtonT2;
  }
  if (REBRAND_TREATMENT === REBRAND_BUTTON_TREATMENT.Treatment3) {
    return RebrandButtonT3;
  }

  return ButtonPPReactV7;
};

export const Button = (props: ButtonProps): React.ReactElement => {
  const {
    atomicEventsAttributes,
    children,
    contentKey,
    customerInteraction,
    disabled,
    interstitial,
    isFormSubmission = false,
    isLoading,
    onClick = () => {},
    secondary = false,
    size = 'lg',
    testID,
    tertiary,
    type = 'submit',
  } = props;
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const isInterstitialOpen = useInterstitialOpen();
  const baseDisabledState = isFormSubmitting || isLoading || disabled;
  const disabledState = interstitial ? disabled : baseDisabledState;

  const { content, keyName } = useSubmitButtonContent();
  const buttonContent = children ? children : content;

  const onClickHandler = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (customerInteraction) {
      CI.start(customerInteraction);
    }
    onClick(event);
  };

  useEffect(() => {
    if ((contentKey || keyName) && !isInterstitialOpen) {
      logFPTICTAContentKey({ contentKey: contentKey || keyName });
    }
  }, [keyName, contentKey]);

  const Component = getComponent(isFormSubmission);

  return (
    <PaypalThemedProvider>
      <Component
        btnState={isLoading && 'processing'}
        data-testid={testID}
        disabled={disabledState}
        onClick={onClickHandler}
        secondary={secondary}
        size={size}
        tertiary={tertiary}
        type={type}
        data-atomic-wait-intent={atomicEventsAttributes?.intent}
        data-atomic-wait-domain={atomicEventsAttributes?.domain}
        data-atomic-wait-task={atomicEventsAttributes?.task}
        data-atomic-wait-viewname={atomicEventsAttributes?.viewName}
        data-atomic-wait-flow={atomicEventsAttributes?.flow}
      >
        {buttonContent}
      </Component>
    </PaypalThemedProvider>
  );
};
